// Linha digitável (boleto bancário 47 / arrecadação 48 dígitos) e PIX copia-e-cola (EMV)
// extraídos do texto OCR com VALIDAÇÃO matemática — dígitos verificadores (mod10/mod11)
// na linha digitável e CRC16-CCITT no PIX. O lote JUN/2026 mostrou por que regex "cega"
// não basta:
//   • o PIX carrega espaços DENTRO do payload ("5916EQUATORIAL GOIAS") — remover todo
//     whitespace corrompia o código (498/570 com CRC inválido);
//   • fatura em débito automático imprime linha de ARRECADAÇÃO (48 dígitos) no formato
//     visual de boleto bancário ("83670.00000 ... 2 603001500121114") — a regex de 47
//     dígitos comia o último dígito;
//   • fatura sem "ficha de compensação" imprime a arrecadação em 4 blocos de 12 dígitos
//     em LINHAS separadas, com o PIX e datas no meio deles.

const DEBITO_RE = /FATURA COM LAN[ÇC]AMENTO PARA D[ÉE]BITO AUTOM[ÁA]TICO|N[ÃA]O RECEBER[^\n]{0,60}D[ÉE]BITO AUTOM[ÁA]TICO/i;

/** true quando a fatura declara cobrança via débito automático em conta corrente. */
export function detectaDebitoAutomatico(text: string): boolean {
  return DEBITO_RE.test(text);
}

const AGRUPAMENTO_RE = /PERTENCENTE\s+A\s+UM\s+AGRUPAMENTO\s+DE\s+CONTAS/i;

/** true quando a fatura declara que pertence a um agrupamento de contas — a cobrança sai
 *  no boleto do agrupamento. Sozinho não dispensa a linha digitável: a 2ª via individual
 *  da mesma UC traz a frase E a linha (ver reconciliação). */
export function detectaAgrupamentoContas(text: string): boolean {
  return AGRUPAMENTO_RE.test(text);
}

// ─────────────────────────── dígitos verificadores ───────────────────────────

/** DV módulo 10 (campos do boleto bancário e blocos de arrecadação moeda 6/7). */
export function mod10(seq: string): number {
  let s = 0, w = 2;
  for (let i = seq.length - 1; i >= 0; i--) {
    const p = Number(seq[i]) * w;
    s += p > 9 ? p - 9 : p;
    w = w === 2 ? 1 : 2;
  }
  return (10 - (s % 10)) % 10;
}

/** DV módulo 11 dos blocos de arrecadação (moeda 8/9). Resto 0/1 → DV 0. */
export function mod11Arrecadacao(seq: string): number {
  let w = 2, s = 0;
  for (let i = seq.length - 1; i >= 0; i--) {
    s += Number(seq[i]) * w;
    w = w === 9 ? 2 : w + 1;
  }
  const r = s % 11;
  return r === 0 || r === 1 ? 0 : 11 - r;
}

/** Valida os DVs de uma linha digitável: 47 dígitos (boleto bancário, 3 campos mod10)
 *  ou 48 dígitos (arrecadação: começa com 8, 4 blocos de 12 com DV mod10/mod11). */
export function validaLinhaDigitavel(digits: string): boolean {
  if (digits.length === 47) {
    const campos = [digits.slice(0, 10), digits.slice(10, 21), digits.slice(21, 32)];
    return campos.every((c) => mod10(c.slice(0, -1)) === Number(c[c.length - 1]));
  }
  if (digits.length === 48) {
    if (digits[0] !== '8') return false;
    const dv = digits[2] === '6' || digits[2] === '7' ? mod10 : mod11Arrecadacao;
    for (let i = 0; i < 4; i++) {
      const b = digits.slice(i * 12, i * 12 + 12);
      if (dv(b.slice(0, 11)) !== Number(b[11])) return false;
    }
    return true;
  }
  return false;
}

/** Valor em reais embutido numa linha digitável que fecha os DVs. `null` quando a linha
 *  é inválida ou não carrega valor:
 *   • 47 dígitos (bancário): os 10 últimos são o valor em centavos; zero = "valor
 *     informado no pagamento".
 *   • 48 dígitos (arrecadação): sem o DV de cada bloco sobra o código de barras de 44.
 *     O 3º dígito diz o que vem nas posições 5–15: valor em reais (6/7) ou referência
 *     (8/9), que não é valor nenhum. */
export function valorDaLinhaDigitavel(digits: string): number | null {
  if (!validaLinhaDigitavel(digits)) return null;
  let centavos: number;
  if (digits.length === 47) {
    centavos = Number(digits.slice(37));
  } else {
    if (digits[2] !== '6' && digits[2] !== '7') return null;
    const barras = [0, 1, 2, 3].map((i) => digits.slice(i * 12, i * 12 + 11)).join('');
    centavos = Number(barras.slice(4, 15));
  }
  return centavos > 0 ? centavos / 100 : null;
}

/** Formata p/ exibição: 47 → campos do boleto bancário; 48 → 4 blocos de 12. */
export function formataLinhaDigitavel(digits: string): string {
  if (digits.length === 47) {
    return `${digits.slice(0, 5)}.${digits.slice(5, 10)} ${digits.slice(10, 15)}.${digits.slice(15, 21)} ${digits.slice(21, 26)}.${digits.slice(26, 32)} ${digits.slice(32, 33)} ${digits.slice(33)}`;
  }
  if (digits.length === 48) {
    return `${digits.slice(0, 12)} ${digits.slice(12, 24)} ${digits.slice(24, 36)} ${digits.slice(36)}`;
  }
  return digits;
}

/** Extrai a linha digitável do texto OCR. Gera candidatos por 3 caminhos e aceita o
 *  primeiro que passa nos DVs; sem candidato válido, devolve o melhor palpite com
 *  valida=false (a reconciliação sinaliza). */
export function extraiLinhaDigitavel(text: string): { linha: string; valida: boolean } {
  const candidatos: string[] = [];
  const push = (digits: string) => { if (!candidatos.includes(digits)) candidatos.push(digits); };

  // (a) formato impresso de boleto bancário — o último campo pode vir com 15 dígitos
  //     (arrecadação de 48 disfarçada: "… 2 603001500121114").
  const cbm = /(\d{5})[.\s]?(\d{5})\s+(\d{5})[.\s]?(\d{6})\s+(\d{5})[.\s]?(\d{6})\s*(\d)\s*(\d{14,15})/.exec(text);
  if (cbm) push(cbm.slice(1).join(''));

  // (a2) corrida CONTÍGUA de 47/48 dígitos em qualquer lugar do texto — a fatura de
  //      cobrança da associação (layout antigo) imprime "LINHA DIGITÁVEL / CÓDIGO DE
  //      BARRAS: 7569130433011..." sem pontuação nenhuma, colada ao rótulo. Os
  //      lookarounds impedem casar um pedaço de corrida maior (chave de acesso é 44).
  const runRe = /(?<!\d)(\d{47,48})(?!\d)/g;
  let runM: RegExpExecArray | null;
  while ((runM = runRe.exec(text)) !== null) push(runM[1]);

  // (b) linhas "só dígitos" (com pontos/espaços): uma linha ou junção de consecutivas
  //     somando 47/48 dígitos (pega o pseudo-formato colado e quebras simples).
  const lines = text.split('\n');
  const digitosLinha = lines.map((l) => (/^[\d.\s\-]+$/.test(l.trim()) && l.trim() ? l.replace(/\D/g, '') : null));
  for (let i = 0; i < lines.length; i++) {
    if (!digitosLinha[i]) continue;
    let acc = '';
    for (let j = i; j < Math.min(i + 5, lines.length); j++) {
      const d = digitosLinha[j];
      if (d === null) break;
      acc += d;
      if (acc.length === 47 || acc.length === 48) { push(acc); break; }
      if (acc.length > 48) break;
    }
  }

  // (b2) linha de dígitos MAIOR que 48: no sub-layout de 2025 a linha digitável sai
  //      colada ao código do banco ("341-7 34191.09768 33519.70293185633.1500095
  //      12360000007086" = 51 dígitos), então nenhuma janela fixa casa e a fatura ficava
  //      sem código de barras. Varremos as janelas de 47/48 dígitos e só aceitamos as que
  //      FECHAM os DVs — recorte que não valida nunca vira candidato.
  for (const d of digitosLinha) {
    if (!d || d.length <= 48 || d.length > 64) continue;
    for (let k = 0; k + 47 <= d.length; k++) {
      for (const len of [47, 48]) {
        const w = d.slice(k, k + len);
        if (w.length === len && validaLinhaDigitavel(w)) push(w);
      }
    }
  }

  // (c) arrecadação em 4 blocos de 12 dígitos "soltos" (linhas separadas, podendo haver
  //     texto no meio — na fatura real o PIX e o vencimento ficam ENTRE os blocos).
  const blocos: Array<{ d: string; idx: number }> = [];
  const blocoRe = /(?<!\d)(\d{12})(?!\d)/g;
  let bm: RegExpExecArray | null;
  while ((bm = blocoRe.exec(text)) !== null) blocos.push({ d: bm[1], idx: bm.index });
  for (let i = 0; i + 3 < blocos.length; i++) {
    if (blocos[i].d[0] !== '8') continue;
    if (blocos[i + 3].idx - blocos[i].idx > 800) continue; // blocos da mesma região da página
    push(blocos[i].d + blocos[i + 1].d + blocos[i + 2].d + blocos[i + 3].d);
  }

  // (d) fallback antigo: 4 grupos de 11–12 dígitos na mesma linha começando com 8
  const cv = /\b(8\d{10,11})[.\s]+(\d{11,12})[.\s]+(\d{11,12})[.\s]+(\d{11,12})\b/.exec(text);
  if (cv) push(cv.slice(1).join(''));

  for (const digits of candidatos) {
    if (validaLinhaDigitavel(digits)) return { linha: formataLinhaDigitavel(digits), valida: true };
  }
  const primeiro = candidatos.find((d) => d.length === 47 || d.length === 48);
  return { linha: primeiro ? formataLinhaDigitavel(primeiro) : '', valida: false };
}

// ─────────────────────────────── PIX (EMV/TLV) ───────────────────────────────

/** CRC16-CCITT (0xFFFF / 0x1021) sobre os bytes UTF-8 do payload, como no BR Code. */
export function crc16(payload: string): string {
  const bytes = new TextEncoder().encode(payload);
  let crc = 0xFFFF;
  for (const b of bytes) {
    crc ^= b << 8;
    for (let i = 0; i < 8; i++) crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xFFFF : (crc << 1) & 0xFFFF;
  }
  return crc.toString(16).toUpperCase().padStart(4, '0');
}

/** true se o payload termina em "6304" + CRC coerente com o corpo. */
export function pixCrcOk(payload: string): boolean {
  const idx = payload.lastIndexOf('6304');
  if (idx < 0 || idx + 8 !== payload.length) return false;
  return crc16(payload.slice(0, idx + 4)) === payload.slice(idx + 4).toUpperCase();
}

/** Caminha a estrutura TLV a partir de `start`: tag(2) + len(2) + valor(len).
 *  Quebras de linha são artefato de OCR (puladas sempre); espaço/tab só é pulado FORA
 *  de valor — dentro do valor, espaço é conteúdo ("EQUATORIAL GOIAS"). Termina na tag
 *  63 (CRC, len 4). Retorna null se a estrutura não fecha. */
function walkEmv(text: string, start: number): { payload: string; crcOk: boolean } | null {
  let i = start;
  const next = (emValor: boolean): string | null => {
    while (i < text.length) {
      const ch = text[i];
      if (ch === '\n' || ch === '\r') { i++; continue; }
      if (!emValor && (ch === ' ' || ch === '\t')) { i++; continue; }
      i++;
      return ch;
    }
    return null;
  };
  let payload = '';
  while (payload.length <= 512) {
    let tag = '', len = '';
    for (let k = 0; k < 2; k++) { const c = next(false); if (!c || !/\d/.test(c)) return null; tag += c; }
    for (let k = 0; k < 2; k++) { const c = next(false); if (!c || !/\d/.test(c)) return null; len += c; }
    const n = parseInt(len, 10);
    if (!n) return null;
    let valor = '';
    for (let k = 0; k < n; k++) { const c = next(true); if (c === null) return null; valor += c; }
    payload += tag + len + valor;
    if (tag === '63') {
      if (n !== 4) return null;
      return { payload, crcOk: crc16(payload.slice(0, -4)) === valor.toUpperCase() };
    }
  }
  return null;
}

// ───────────────── correção de misread de OCR verificada pelo CRC ─────────────────
// O Document AI erra caracteres nas partes VARIÁVEIS do payload (uuid da cobrança,
// txid numérico, o próprio CRC) — ex. "…070e45" lido como "…070045", "1a0debbb" como
// "la0debbb". O CRC impresso funciona como verificador de 16 bits: testamos correções
// de UM caractere nessas regiões e só aceitamos quando EXATAMENTE UMA correção fecha
// o CRC (ambiguidade → mantém inválido para revisão humana).

const HEX = '0123456789abcdef';
const HOMOGLIFO: Record<string, string> = {
  l: '1', I: '1', i: '1', O: '0', o: '0', S: '5', s: '5', Z: '2', z: '2', G: '6', g: '9', q: '9', t: '7', T: '7', B: '8',
};

// O OCR também troca letras por CIRÍLICO/GREGO visualmente idênticos — casos reais do
// lote JUN/2026: "GOIANIA" impresso virou "GOIAΝΙΑ" (grego) e o uuid "a8ec-ec6ae2"
// virou "авес-есбае2" (cirílico). São chars > U+007F, impossíveis num BR Code legítimo
// (payload é ASCII), então a substituição global é segura — e o CRC confirma no fim.
const CONFUSAVEL_NAO_ASCII: Record<string, string> = {
  // cirílico minúsculo (в=8 e б=6 observados no lote: o glifo lembra o dígito)
  'а': 'a', 'е': 'e', 'о': 'o', 'с': 'c', 'р': 'p', 'х': 'x', 'у': 'y', 'к': 'k', 'б': '6', 'в': '8', 'з': '3',
  // cirílico maiúsculo
  'А': 'A', 'В': 'B', 'Е': 'E', 'К': 'K', 'М': 'M', 'Н': 'H', 'О': 'O', 'Р': 'P', 'С': 'C', 'Т': 'T', 'Х': 'X', 'З': '3', 'Б': '6',
  // grego maiúsculo
  'Α': 'A', 'Β': 'B', 'Ε': 'E', 'Ζ': 'Z', 'Η': 'H', 'Ι': 'I', 'Κ': 'K', 'Μ': 'M', 'Ν': 'N', 'Ο': 'O', 'Ρ': 'P', 'Τ': 'T', 'Υ': 'Y', 'Χ': 'X',
  // grego minúsculo
  'α': 'a', 'ο': 'o', 'ε': 'e', 'ι': 'i', 'κ': 'k', 'ν': 'v', 'ρ': 'p', 'τ': 't', 'υ': 'u', 'χ': 'x',
};

function normalizaConfusaveis(s: string): string {
  let out = '';
  for (const ch of s) out += ch.charCodeAt(0) > 127 && CONFUSAVEL_NAO_ASCII[ch] ? CONFUSAVEL_NAO_ASCII[ch] : ch;
  return out;
}

/** Spans (início/fim exclusivo) das regiões variáveis do payload: valor da tag 26 após
 *  o último '/', valor da tag 62 e valor da tag 63 (CRC). Payload precisa ser TLV são. */
function regioesVariaveis(payload: string): Array<{ ini: number; fim: number; alfabeto: string }> {
  const out: Array<{ ini: number; fim: number; alfabeto: string }> = [];
  let i = 0;
  while (i + 4 <= payload.length) {
    const tag = payload.slice(i, i + 2);
    const len = parseInt(payload.slice(i + 2, i + 4), 10);
    if (!/^\d\d$/.test(tag) || isNaN(len)) break;
    const vIni = i + 4, vFim = vIni + len;
    if (tag === '26' || tag === '25' || tag === '80') {
      // tag 80 = template do Pix Automático ("…/pix/v2/rec/<uuid>") — mesma anatomia
      // da 26: só o trecho após a última barra é variável (misreads reais: "albe38d3"
      // por "a1be38d3", "Od0fb28e" por "0d0fb28e").
      const val = payload.slice(vIni, vFim);
      const barra = val.lastIndexOf('/');
      if (barra >= 0) out.push({ ini: vIni + barra + 1, fim: vFim, alfabeto: HEX + '-' });
    } else if (tag === '62') {
      out.push({ ini: vIni, fim: vFim, alfabeto: '0123456789' });
    } else if (tag === '63') {
      out.push({ ini: vIni, fim: vFim, alfabeto: '0123456789ABCDEF' });
    }
    i = vFim;
  }
  return out;
}

function crcConfere(payload: string): boolean {
  const idx = payload.length - 8;
  return payload.slice(idx, idx + 4) === '6304' && crc16(payload.slice(0, idx + 4)) === payload.slice(idx + 4).toUpperCase();
}

/** Tenta consertar UM caractere mal lido (homóglifos primeiro, depois busca exaustiva
 *  nas regiões variáveis). Devolve o payload corrigido apenas se a correção for única. */
function tentaCorrigirPix(payload: string): string | null {
  const regioes = regioesVariaveis(payload);
  if (!regioes.length) return null;

  // passo 1: homóglifos (chars fora do alfabeto da região) — pode consertar vários de uma vez
  let base = payload;
  for (const { ini, fim, alfabeto } of regioes) {
    for (let p = ini; p < fim && p < base.length; p++) {
      const c = base[p];
      if (!alfabeto.includes(c) && !alfabeto.includes(c.toLowerCase()) && HOMOGLIFO[c]) {
        base = base.slice(0, p) + HOMOGLIFO[c] + base.slice(p + 1);
      }
    }
  }
  if (crcConfere(base)) return base;

  // passo 1.5: tag 58 (código do país) é FIXA = "BR" por definição do BR Code — misread
  // real do lote: "58028R" ("B" lido como "8"). Conserto determinístico validado pelo CRC.
  {
    let i = 0, fix = base;
    while (i + 4 <= fix.length) {
      const tag = fix.slice(i, i + 2);
      const len = parseInt(fix.slice(i + 2, i + 4), 10);
      if (!/^\d\d$/.test(tag) || isNaN(len)) break;
      if (tag === '58' && len === 2 && fix.slice(i + 4, i + 6) !== 'BR') {
        fix = fix.slice(0, i + 4) + 'BR' + fix.slice(i + 6);
      }
      if (tag === '63') break;
      i += 4 + len;
    }
    if (fix !== base && crcConfere(fix)) return fix;
  }

  // passo 2: busca de 1 caractere dentro das regiões variáveis, exigindo solução única
  const solucoes = new Set<string>();
  for (const cand of base === payload ? [payload] : [payload, base]) {
    for (const { ini, fim, alfabeto } of regioes) {
      for (let p = ini; p < fim && p < cand.length; p++) {
        for (const ch of alfabeto) {
          if (ch === cand[p]) continue;
          const v = cand.slice(0, p) + ch + cand.slice(p + 1);
          if (crcConfere(v)) solucoes.add(v);
          if (solucoes.size > 1) return null;
        }
      }
    }
  }
  return solucoes.size === 1 ? [...solucoes][0] : null;
}

// ───────────── reparo estrutural: caractere INSERIDO ou COMIDO pelo OCR ─────────────
// Quando a caminhada TLV nem fecha (ex.: uuid "a0Oea" com 'O' espúrio, grupo "400" com
// dígito comido, "58028BR" com '8' a mais), os comprimentos declarados desalinham e o
// payload inteiro se perdia. Testamos deletar/inserir UM caractere e só aceitamos a
// solução ÚNICA cuja estrutura fecha E o CRC confere (mesma filosofia do tentaCorrigirPix).
function reparaEstruturaEmv(text: string, start: number): { payload: string; crcOk: boolean } | null {
  // janela compacta a partir da âncora, sem quebras de linha (espaços são conteúdo)
  const janela = text.slice(start, start + 480).replace(/[\r\n]+/g, '');
  // fim candidato: "6304" + 4 hex (tag do CRC); varre do último para o primeiro
  const fins = [...janela.matchAll(/6304[0-9A-Fa-f]{4}/g)];
  for (let e = fins.length - 1; e >= 0; e--) {
    const cand = janela.slice(0, (fins[e].index ?? 0) + 8);
    if (cand.length < 40) continue;
    const sols = new Set<string>();
    // deleção de 1 caractere (OCR inseriu um char espúrio)
    for (let p = 6; p < cand.length - 8 && sols.size <= 1; p++) {
      const r = walkEmv(cand.slice(0, p) + cand.slice(p + 1), 0);
      if (r?.crcOk) sols.add(r.payload);
    }
    if (sols.size === 1) return { payload: [...sols][0], crcOk: true };
    if (sols.size > 1) continue; // ambíguo → não inventa
    // inserção de 1 caractere (OCR comeu um char) — alfabeto das regiões variáveis
    for (let p = 6; p <= cand.length - 8 && sols.size <= 1; p++) {
      for (const ch of HEX + '-') {
        const r = walkEmv(cand.slice(0, p) + ch + cand.slice(p), 0);
        if (r?.crcOk) sols.add(r.payload);
        if (sols.size > 1) break;
      }
    }
    if (sols.size === 1) return { payload: [...sols][0], crcOk: true };
  }
  return null;
}

/** Extrai o PIX copia-e-cola. Âncora em "000201" (indicador de formato EMV — a fatura
 *  sem ficha de compensação NÃO traz o campo "0102..." e a âncora antiga "00020101"
 *  perdia esses PIX). Prefere payload com CRC válido (corrigindo misread de OCR quando
 *  a correção é única); senão, o primeiro estruturalmente completo que contenha
 *  br.gov.bcb.pix (evita casar sequência numérica aleatória). */
export function extraiPix(textRaw: string): { pix: string; valido: boolean } {
  // cirílico/grego visualmente idêntico vira ASCII antes de tudo (o CRC confirma depois)
  const text = normalizaConfusaveis(textRaw);
  const re = /000201/g;
  let melhor: { pix: string; valido: boolean } | null = null;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const r = walkEmv(text, m.index) ?? reparaEstruturaEmv(text, m.index);
    if (!r || !/br\.gov\.bcb\.pix/i.test(r.payload)) continue;
    if (r.crcOk) return { pix: r.payload, valido: true };
    const corrigido = tentaCorrigirPix(r.payload);
    if (corrigido) return { pix: corrigido, valido: true };
    if (!melhor) melhor = { pix: r.payload, valido: false };
  }
  return melhor ?? { pix: '', valido: false };
}
